import { useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  HUB_DOCUMENT_TITLE,
  HUB_FAVICON,
  HUB_FAVICON_DATA_URI,
  isHubAreaPath,
  SITE_DOCUMENT_TITLE,
  SITE_FAVICON
} from '../constants/favicons'

const DATA_ATTR = 'data-hub-favicon'
const FAVICON_VERSION = 'hub-dark-green-v2'
const HUB_FONT_LINK_ID = 'viva-hub-fonts'
const HUB_ROOT_CLASS = 'viva-hub'
const HUB_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Source+Sans+3:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap'

function ensureHubFonts () {
  if (document.getElementById(HUB_FONT_LINK_ID)) return
  const link = document.createElement('link')
  link.id = HUB_FONT_LINK_ID
  link.rel = 'stylesheet'
  link.href = HUB_FONTS_HREF
  document.head.appendChild(link)
}

function removeHubFonts () {
  document.getElementById(HUB_FONT_LINK_ID)?.remove()
}

function setHubRootClass (enabled) {
  document.documentElement.classList.toggle(HUB_ROOT_CLASS, enabled)
}

function withCacheBust (href) {
  const sep = href.includes('?') ? '&' : '?'
  return `${href}${sep}v=${FAVICON_VERSION}`
}

function createFaviconLink ({ rel, sizes, href, type }) {
  const link = document.createElement('link')
  link.setAttribute('rel', rel)
  link.setAttribute(DATA_ATTR, '')
  link.setAttribute('href', href)
  if (sizes) link.setAttribute('sizes', sizes)
  if (type) link.setAttribute('type', type)
  return link
}

function upsertFaviconLink ({ rel, sizes, href, type, reuseExisting = false }) {
  const sizeSelector = sizes ? `[sizes="${sizes}"]` : ':not([sizes])'
  let link = document.querySelector(`link[rel="${rel}"]${sizeSelector}[${DATA_ATTR}]`)

  if (!link && reuseExisting) {
    link = document.querySelector(`link[rel="${rel}"]${sizeSelector}`)
  }

  if (!link) {
    link = createFaviconLink({ rel, sizes, href, type })
    document.head.appendChild(link)
    return
  }

  link.setAttribute(DATA_ATTR, '')
  link.setAttribute('href', href)
  if (type) link.setAttribute('type', type)
}

function removeHubFaviconLinks () {
  document
    .querySelectorAll(
      `link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[${DATA_ATTR}]`
    )
    .forEach((link) => link.remove())
}

function applySiteHeadDefaults () {
  document.title = SITE_DOCUMENT_TITLE

  removeHubFaviconLinks()

  upsertFaviconLink({
    rel: 'icon',
    sizes: null,
    href: SITE_FAVICON.ico,
    type: 'image/x-icon',
    reuseExisting: true
  })
  upsertFaviconLink({
    rel: 'icon',
    sizes: '32x32',
    href: SITE_FAVICON.png32,
    type: 'image/png',
    reuseExisting: true
  })
  upsertFaviconLink({
    rel: 'icon',
    sizes: '16x16',
    href: SITE_FAVICON.png16,
    type: 'image/png',
    reuseExisting: true
  })
  upsertFaviconLink({
    rel: 'apple-touch-icon',
    sizes: '180x180',
    href: SITE_FAVICON.apple,
    type: 'image/png',
    reuseExisting: true
  })
}

function applyHubHead () {
  document.title = HUB_DOCUMENT_TITLE
  removeHubFaviconLinks()

  // Prepend so hub icons win over any stale defaults; inline SVG avoids file cache.
  const specs = [
    { rel: 'icon', type: 'image/svg+xml', href: HUB_FAVICON_DATA_URI },
    { rel: 'shortcut icon', type: 'image/x-icon', href: withCacheBust(HUB_FAVICON.ico) },
    { rel: 'icon', type: 'image/x-icon', href: withCacheBust(HUB_FAVICON.ico) },
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: withCacheBust(HUB_FAVICON.png32) },
    { rel: 'icon', type: 'image/png', sizes: '16x16', href: withCacheBust(HUB_FAVICON.png16) },
    {
      rel: 'apple-touch-icon',
      type: 'image/png',
      sizes: '180x180',
      href: withCacheBust(HUB_FAVICON.apple)
    }
  ]

  for (const spec of specs.reverse()) {
    document.head.insertBefore(createFaviconLink(spec), document.head.firstChild)
  }
}

/**
 * Swaps favicon assets and document title between the public site and VIVA HUB.
 */
export function useHubFavicon () {
  const { pathname } = useLocation()

  useLayoutEffect(() => {
    if (isHubAreaPath(pathname)) {
      setHubRootClass(true)
      ensureHubFonts()
      applyHubHead()
      return
    }

    setHubRootClass(false)
    removeHubFonts()
    applySiteHeadDefaults()
  }, [pathname])
}
